<script setup lang="ts">
import type { Schemas } from '#shopware'

interface ProductListItemProps {
    product: Schemas['Product']
    variant: 'wishlist' | 'clickAndReserve'
}

const props = defineProps<ProductListItemProps>()

const emit = defineEmits<{
    productClick: [product: Schemas['Product']]
}>()

// Composables
const { success, error: showError } = useGlobalNotifications()
const { t } = useI18n()
const localePath = useLocalePath()

// Initialize composables at setup level based on variant
const wishlistComposable = props.variant === 'wishlist' ? useProductWishlist(props.product.id) : null
const clickAndReserveComposable = props.variant === 'clickAndReserve' ? useClickAndReserve() : null

// Variant-specific configuration
const config = computed(() => {
    if (props.variant === 'wishlist') {
        return {
            successMessage: 'wishlist.messages.removedFromWishlist',
            errorMessage: 'wishlist.messages.wishlistError'
        }
    } else {
        return {
            successMessage: 'clickAndReserve.messages.removedFromClickAndReserve',
            errorMessage: 'clickAndReserve.messages.error'
        }
    }
})

// Remove product from list based on variant
const onRemove = async () => {
    try {
        if (props.variant === 'wishlist' && wishlistComposable) {
            await wishlistComposable.removeFromWishlist()
        } else if (props.variant === 'clickAndReserve' && clickAndReserveComposable) {
            clickAndReserveComposable.removeFromClickAndReserve(props.product.id)
        }
        success(t(config.value.successMessage, { product: props.product.translated?.name || '' }))
    } catch (e) {
        console.error(`Failed to remove from ${props.variant}:`, e)
        showError(t(config.value.errorMessage))
    }
}

// Handle product click
const handleClick = () => {
    // Always emit event for parent component (sidenav will handle this)
    emit('productClick', props.product)
    navigateTo(localePath('/' + props.product.seoUrls?.[0]?.seoPathInfo))
}
</script>

<template>
    <div class="flex gap-4 items-center">
        <!-- Product image -->
        <button
            class="relative flex-shrink-0 w-20 h-20 cursor-pointer"
            :aria-label="$t('product.listItem.viewProduct', { name: product.translated?.name })"
            @click="handleClick"
        >
            <ProductListItemImage :product="product" />
        </button>

        <!-- Product details -->
        <div class="flex-1 min-w-0">
            <div class="flex justify-between h-full">
                <div class="flex flex-col justify-center">
                    <button
                        class="text-left cursor-pointer group"
                        @click="handleClick"
                    >
                        <FoundationHeadline tag="h3" class="text-sm font-medium group-hover:underline">
                            {{ product.translated?.name }}
                        </FoundationHeadline>
                    </button>

                    <!-- Product variant details if available -->
                    <div v-if="product.options?.length" class="mt-1">
                        <p 
                            v-for="(option, index) in product.options" 
                            :key="index" 
                            class="text-xs text-base-600" 
                        >
                            {{ option.group?.translated?.name }}: {{ option.translated?.name }}
                        </p>
                    </div>

                    <ProductPrice
                        :product="product"
                        size="small"
                        alignment="start"
                        class="min-h-10 mt-1"
                    />
                </div>

                <div class="flex flex-col justify-between items-end gap-2">
                    <div>
                        <FoundationButton
                            class="text-base font-bold underline px-0"
                            size="small"
                            :aria-label="$t('product.listItem.details')"
                            @click="handleClick"
                        >
                            {{ $t('product.listItem.details') }}
                        </FoundationButton>
                    </div>

                    <!-- Remove button -->
                    <div class="flex justify-end">
                        <FoundationButton
                            class="text-base font-bold underline px-0"
                            size="small"
                            :aria-label="$t('product.listItem.remove')"
                            @click="onRemove"
                        >
                            {{ $t('product.listItem.remove') }}
                        </FoundationButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
